import { useEffect, useState } from "react";

import { logError, SDKError } from "@smart/itops-utils-basic";

import { OpenRequest, UseSDKApiOptions, Version } from "../types";

export const useHost = ({ client, isInit, hostType }: UseSDKApiOptions) => {
  const [version, setVersion] = useState<Version>();

  useEffect(() => {
    if (isInit && client) {
      client.host
        .version()
        .then(setVersion)
        .catch((err) => logError(new SDKError(err, { action: "version.get" })));
    }
  }, [isInit]);

  if (!isInit || !client) return undefined;

  return {
    close: () => {
      try {
        client.host.close();
      } catch (error) {
        throw new SDKError(error, { action: "host.close" });
      }
    },
    interceptClose: (
      callback: (continueClose: (shouldClose: boolean) => void) => void,
    ) => {
      try {
        client.host.interceptClose(callback);
      } catch (error) {
        throw new SDKError(error, { action: "host.interceptClose" });
      }
    },
    selectTab: (tab: number) => {
      try {
        client.host.selectTab(tab);
      } catch (error) {
        throw new SDKError(error, { action: "host.selectTab", input: { tab } });
      }
    },
    version,
    isEarlierThanVersion: ({
      major,
      minor,
    }: {
      major: number;
      minor: number;
    }) => {
      if (!version) return undefined;
      if (version.major === major) return version.minor < minor;
      return version.major < major;
    },
    open: (options: OpenRequest) => {
      client.host.open(options);
    },
    navigate: (
      options:
        | {
            target: "intake-form-builder";
            formId?: string;
            reminderMessage?: "custom" | "default";
          }
        | { target: "intake-settings"; tab?: string }
        | {
            target: "matter-intake-tab";
            matterId: string;
            submissionId: string;
          }
        | { target: "matter-details"; matterId: string },
    ) => {
      const { matterId } = options as { matterId: string };
      const { submissionId } = options as { submissionId: string };
      const { formId } = options as { formId?: string };
      const { reminderMessage } = options as { reminderMessage?: string };
      const { tab } = options as { tab?: string };
      let intakeParams: string[] | undefined;
      if (reminderMessage) intakeParams = [formId ?? "", reminderMessage];
      else if (formId) intakeParams = [formId];
      let intakeWindowsUrl = "smokeball://intakeforms/";
      if (reminderMessage)
        intakeWindowsUrl = `smokeball://intakeforms?formId=${formId ?? ""}&reminderMessage=${reminderMessage}`;
      else if (formId)
        intakeWindowsUrl = `smokeball://intakeforms?formId=${formId}`;
      const navigationOptionsMapping = {
        "intake-form-builder": {
          web: {
            type: "intake",
            ...(intakeParams ? { params: intakeParams } : {}),
          },
          windows: intakeWindowsUrl,
        },
        "intake-settings": {
          web: { type: "intakeSettings", ...(tab ? { params: [tab] } : {}) },
          windows:
            "smokeball://SmokeballDocuments.SettingsUriProvider?viewId=ItOps.IntakeSettingsViewKey",
        },
        "matter-intake-tab": {
          web: {
            type: "matterIntake",
            id: matterId,
            params: [submissionId],
          },
          windows: `smokeball://MatterManagement.MatterUriProvider?matterId=${matterId}&matterTab=Intake&data=${submissionId}`,
        },
        "matter-details": {
          web: { type: "editMatter", id: matterId },
          windows: `smokeball://MatterManagement.MatterUriProvider?matterId=${matterId}&matterTab=Matter`,
        },
      };
      if (hostType === "web") {
        // Non-standard SDK api support navigating in Smokeball Web (Billing) - LINK-2202
        window.parent.postMessage(
          {
            ...navigationOptionsMapping[options.target].web,
            operation: "host.webNavigate",
          },
          "*",
        );
      } else if (hostType === "windows") {
        window.open(navigationOptionsMapping[options.target].windows);
      }
    },
  };
};
